import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../AuthProvider.tsx";
import PortalSidebar from "../components/PortalSidebar.tsx";
import { buildApiUrl } from "../services/apiConfig.ts";
import "./overview.css";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, useMapEvents, Circle, Tooltip } from "react-leaflet";
import L, { LatLng } from "leaflet";
import "leaflet/dist/leaflet.css";
import { useParams, useNavigate } from "react-router-dom";
import { getCreativeMediaType, isSupportedCreativeFile } from "./campaignCreative.ts";

// Backend currently swaps lon/lat when storing (ST_MakePoint($2,$3) with $2=lat, $3=lon)
// Set this to true to pre-swap on send until server fix lands.
const SWAP_LAT_LON_FOR_API = false; // backend expects ST_MakePoint(longitude, latitude)

// ============================
// Types
// ============================
interface Area {
  id: string; // client-side id
  latitude: number;
  longitude: number;
  radius: number; // meters
}

interface DayRange {
  start_time: string; // HH:MM
  end_time: string;   // HH:MM
}

interface ValueCalculationConfig {
  id: string;
  cpk: number;
  car_price: number;
  view_per_km_estimate: number;
  inserted_at: string;
}

type CampaignFuelTarget = "all" | "electric_and_hybrid" | "electric_only";

const CAMPAIGN_FUEL_TARGET_OPTIONS: Array<{ value: CampaignFuelTarget; label: string }> = [
  { value: "electric_only", label: "EVs" },
  { value: "electric_and_hybrid", label: "Hybride + EVs" },
  { value: "all", label: "Alle" },
];

// ============================
// Helpers
// ============================
const uuid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const toDDMMYYYY = (d: Date) =>
  String(d.getDate()).padStart(2, "0") +
  "/" +
  String(d.getMonth() + 1).padStart(2, "0") +
  "/" +
  d.getFullYear();

// Convert between dd/mm/YYYY and ISO (YYYY-MM-DD) for native date inputs
const ddmmyyyyToISO = (s: string) => {
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
};
const isoToDDMMYYYY = (iso: string) => {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const [, yyyy, mm, dd] = m;
  return `${dd}/${mm}/${yyyy}`;
};

const toISODate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const addDays = (d: Date, days: number) => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const CAMPAIGN_BLOCK_DAYS = 28;
const DEFAULT_CAMPAIGN_MIN_DURATION_DAYS = 28;

const campaignDurationBlocks = (start: string, end: string) => {
  const startIso = ddmmyyyyToISO(start);
  const endIso = ddmmyyyyToISO(end);
  if (!startIso || !endIso) return 1;
  const startDate = new Date(`${startIso}T00:00:00`);
  const endDate = new Date(`${endIso}T00:00:00`);
  const durationDays = Math.max(
    1,
    Math.round((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000))
  );
  return Math.max(1, Math.ceil(durationDays / CAMPAIGN_BLOCK_DAYS));
};

const parseHHMM = (s: string) => {
  const m = s.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  return !!m;
};

const timeLt = (a: string, b: string) => a < b; // lex works for HH:MM

const rangesOverlap = (a: DayRange, b: DayRange) => a.start_time < b.end_time && a.end_time > b.start_time;

const validateRanges = (ranges: DayRange[]) => {
  for (const r of ranges) {
    if (!parseHHMM(r.start_time) || !parseHHMM(r.end_time)) return "Invalid time format. Use HH:MM";
    if (!timeLt(r.start_time, r.end_time)) return "Each start_time must be before end_time.";
  }
  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      if (rangesOverlap(ranges[i], ranges[j])) return "Time ranges overlap.";
    }
  }
  return null;
};

// ============================
// Map interaction component
// ============================
function ClickToAddCircle({ onAdd }: { onAdd: (latlng: LatLng) => void }) {
  useMapEvents({
    click(e) {
      onAdd(e.latlng);
    },
  });
  return null;
}

// ============================
// Wizard
// ============================
const CampaignWizard: React.FC = () => {
  const { fetchWithAuth, isOrgAdmin } = useAuth();
  const { id: editId } = useParams<{ id: string }>();
  const isAdminEdit = Boolean(isOrgAdmin && editId);
  const navigate = useNavigate(); // Add this line

  // Step control
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1: Base ad
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState(""); // dd/mm/YYYY
  const [endDate, setEndDate] = useState("");
  const [budget, setBudget] = useState<number | "">(0);
  const [cars, setCars] = useState<number>(1);
  const [minimumDurationDays, setMinimumDurationDays] = useState(DEFAULT_CAMPAIGN_MIN_DURATION_DAYS);
  const [valueConfig, setValueConfig] = useState<ValueCalculationConfig | null>(null);
  const [carPrice, setCarPrice] = useState<number>(0);
  const [exclusive, setExclusive] = useState(false);
  const [campaignFuelTarget, setCampaignFuelTarget] = useState<CampaignFuelTarget>("all");
  const [creativeFile, setCreativeFile] = useState<File | null>(null);
  const [creativePreviewUrl, setCreativePreviewUrl] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState(""); // <-- Add this line

  // Created ad id
  const [adId, setAdId] = useState<string | null>(null);

  // Step 2: Targeting areas
  const [areas, setAreas] = useState<Area[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);

  // Day times are ad-level in current API; we still show them below the selected area for UX,
  // but they apply globally when submitting.
  const [dayTimes, setDayTimes] = useState<DayRange[]>([{ start_time: "07:00", end_time: "10:00" }]);

  const selectedArea = useMemo(() => areas.find((a) => a.id === selectedAreaId) || null, [areas, selectedAreaId]);

  const fetchValueConfig = useCallback(
    async (params?: { id?: string; at?: string }) => {
      const search = new URLSearchParams();
      if (params?.id) search.set("id", params.id);
      else if (params?.at) search.set("at", params.at);
      const qs = search.toString();
      const res = await fetchWithAuth(buildApiUrl(`/config/value-calculation${qs ? `?${qs}` : ""}`));
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Konnte Konfiguration nicht laden (${res.status}): ${text}`);
      }
      const json = await res.json();
      const cfg = json?.config;
      if (!cfg) {
        throw new Error("Ungültige Konfigurationsantwort");
      }
      return cfg as ValueCalculationConfig;
    },
    [fetchWithAuth]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 1200px)");
    const handler = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    setIsMobile(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener("change", handler);
    } else {
      mq.addListener(handler);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener("change", handler);
      } else {
        mq.removeListener(handler);
      }
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await fetchValueConfig();
        let configuredMinimumDays = DEFAULT_CAMPAIGN_MIN_DURATION_DAYS;
        try {
          const rulesRes = await fetchWithAuth(buildApiUrl("/config/campaign-rules"));
          if (rulesRes.ok) {
            const rules = await rulesRes.json();
            const parsedMinimum = Number(rules?.minimum_duration_days);
            if (Number.isInteger(parsedMinimum) && parsedMinimum > 0) {
              configuredMinimumDays = parsedMinimum;
            }
          }
        } catch {
          // Keep the 28-day default if campaign rules cannot be loaded.
        }
        setMinimumDurationDays(configuredMinimumDays);
        setValueConfig(cfg);
        setCarPrice(cfg.car_price);
        if (!editId) {
          const today = new Date();
          const minStart = addDays(today, 28);
          setStartDate(toDDMMYYYY(minStart));
          setEndDate(toDDMMYYYY(addDays(minStart, configuredMinimumDays)));
          setCars(1);
          setBudget(cfg.car_price);
        }
      } catch (e: any) {
        setErr(e?.message ?? String(e));
      }
    })();
  }, [editId, fetchValueConfig, fetchWithAuth]);

  // Keep budget derived from number of cars
  useEffect(() => {
    if (!carPrice) return;
    const carCount = Math.max(1, Number(cars || 1));
    setBudget(carCount * carPrice * campaignDurationBlocks(startDate, endDate));
  }, [cars, carPrice, startDate, endDate]);

  // Add area on map click
  const handleAddArea = (latlng: LatLng) => {
    const a: Area = { id: uuid(), latitude: latlng.lat, longitude: latlng.lng, radius: 800 };
    setAreas((prev) => [...prev, a]);
    setSelectedAreaId(a.id);
  };

  // Radius adjust with mouse: when selected, scroll to change; drag + Ctrl to fine-tune
  const mapRef = useRef<L.Map | null>(null);
  const onWheelOverCircle = (e: React.WheelEvent, areaId: string) => {
    e.stopPropagation();
    const delta = Math.sign(e.deltaY) * -50; // up to grow, down to shrink
    setAreas((prev) =>
      prev.map((a) => (a.id === areaId ? { ...a, radius: Math.max(50, a.radius + delta) } : a))
    );
    setSelectedAreaId(areaId);
  };

  const handleRadiusSlider = (val: number, areaId: string) => {
    setAreas((prev) => prev.map((a) => (a.id === areaId ? { ...a, radius: val } : a)));
  };

  const removeArea = (areaId: string) => {
    setAreas((prev) => prev.filter((a) => a.id !== areaId));
    if (selectedAreaId === areaId) setSelectedAreaId(null);
  };

  // Validations
  const [err, setErr] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const clearErr = () => setErr(null);

  const validateStep1 = () => {
    const isEdit = !!editId;
    if (!title.trim()) return "Titel ist erforderlich";
    if (!description.trim()) return "Beschreibung ist erforderlich";
    if (!startDate || !endDate) return "Start- und Enddatum sind erforderlich (TT/MM/JJJJ)";
    if (!/^[0-3]\d\/[01]\d\/[0-9]{4}$/.test(startDate) || !/^[0-3]\d\/[01]\d\/[0-9]{4}$/.test(endDate))
      return "Daten müssen im Format TT/MM/JJJJ sein";
    if (!isAdminEdit) {
      const parsedStart = new Date(ddmmyyyyToISO(startDate));
      const minStart = addDays(new Date(), 28);
      minStart.setHours(0, 0, 0, 0);
      if (parsedStart < minStart) return "Startdatum muss mindestens 28 Tage in der Zukunft liegen";
      const durationDays = Math.round(
        (new Date(ddmmyyyyToISO(endDate)).getTime() - parsedStart.getTime()) / (24 * 60 * 60 * 1000)
      );
      if (durationDays < minimumDurationDays) {
        return `Die Kampagne muss mindestens ${minimumDurationDays} Tage dauern`;
      }
      if (budget === "" || Number(budget) <= 0) return "Budget muss > 0 sein";
    }
    if (!isEdit && !creativeFile) return "Ein Creative als SVG oder PNG-Datei ist erforderlich";
    if (creativeFile && !isSupportedCreativeFile(creativeFile)) return "Es sind nur SVG- oder PNG-Dateien erlaubt";
    if (redirectUrl && !/^https?:\/\/.+/.test(redirectUrl)) return "Weiterleitungs-URL muss mit http:// oder https:// beginnen";
    return null;
  };

  const validateStep2 = () => {
    if (!areas.length) return "Füge mindestens ein Gebiet hinzu (Klick auf die Karte).";
    const tr = validateRanges(dayTimes);
    if (tr) return tr;
    return null;
  };

  // API Calls
  const createAd = async () => {
    if (!valueConfig) throw new Error("Konfiguration nicht geladen");
    const budgetNumber = Number(budget) || 0;
    const form = new FormData();
    form.set("start_date", startDate);
    form.set("end_date", endDate);
    form.set("budget", String(budgetNumber));
    form.set("cpk", String(valueConfig.cpk));
    form.set("value_config_id", valueConfig.id);
    form.set("exclusive", String(exclusive));
    form.set("description", description);
    form.set("title", title);
    form.set("redirect_url", redirectUrl); // <-- Add this line
    form.set("campaign_fuel_target", campaignFuelTarget);
    if (creativeFile) form.set("image", creativeFile, creativeFile.name);

    const res = await fetchWithAuth(buildApiUrl("/ad"), { method: "POST", body: form });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Create ad failed (${res.status}): ${t}`);
    }
    const json = await res.json();
    return json.ad_id as string;
  };

  const updateAd = async (id: string) => {
    if (!valueConfig) throw new Error("Konfiguration nicht geladen");
    const budgetNumber = Number(budget) || 0;
    const form = new FormData();
    form.set("id", id);
    form.set("start_date", startDate);
    form.set("end_date", endDate);
    form.set("budget", String(budgetNumber));
    form.set("cpk", String(valueConfig.cpk));
    form.set("value_config_id", valueConfig.id);
    form.set("exclusive", String(exclusive));
    form.set("description", description);
    form.set("title", title);
    form.set("redirect_url", redirectUrl);
    form.set("campaign_fuel_target", campaignFuelTarget);
    if (creativeFile) {
      form.set("image", creativeFile, creativeFile.name);
    }

    const res = await fetchWithAuth(buildApiUrl("/ad"), { method: "PUT", body: form });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Update ad failed (${res.status}): ${t}`);
    }
  };

  const pushMetadata = async (id: string) => {
    // Prepare locations with temporary lat/lon swap if needed
    const locations = areas.map((a) => ({
      latitude: SWAP_LAT_LON_FOR_API ? a.longitude : a.latitude,
      longitude: SWAP_LAT_LON_FOR_API ? a.latitude : a.longitude,
      radius: a.radius,
    }));

    const payload = { id, locations, day_times: dayTimes };
    const res = await fetchWithAuth(buildApiUrl("/ad/metadata"), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Update metadata failed (${res.status}): ${t}`);
    }
  };

  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    setBusy(true);
    setErr(null);
    try {
      let id = adId;
      if (!id) {
        const e1 = validateStep1();
        if (e1) throw new Error(e1);
        id = await createAd();
        setAdId(id);
      } else {
        const e1 = validateStep1();
        if (e1) throw new Error(e1);
        await updateAd(id);
      }
      const e2 = validateStep2();
      if (e2) throw new Error(e2);
      await pushMetadata(id!);
      setDone(true);
      setStep(3);
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  // Fetch and pre-fill if editing
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        // 1. Fetch all ads and find the one by id (no /ad/by_id endpoint available)
        const res = await fetchWithAuth(buildApiUrl("/ad"));
        if (!res.ok) throw new Error(`Konnte Kampagnenliste nicht laden: ${res.status}`);
        const json = await res.json();
        const ad = (json.ads || []).find((x: any) => String(x.id) === String(editId));
        if (!ad) throw new Error("Kampagne nicht gefunden");

        setTitle(ad.title || "");
        setDescription(ad.description || "");
        setStartDate(ad.start_date || "");
        setEndDate(ad.end_date || "");
        setExclusive(ad.exclusive || false);
        setCampaignFuelTarget((ad.campaign_fuel_target || "all") as CampaignFuelTarget);
        setAdId(ad.id);
        setRedirectUrl(ad.redirect_url || "");

        let cfg: ValueCalculationConfig | null = null;
        if (ad.value_config_id) {
          try {
            cfg = await fetchValueConfig({ id: ad.value_config_id });
          } catch (e) {
            console.warn("Konfiguration über ID nicht gefunden", e);
          }
        }
        if (!cfg && ad.value_config_inserted_at) {
          try {
            cfg = await fetchValueConfig({ at: ad.value_config_inserted_at });
          } catch (e) {
            console.warn("Konfiguration über Zeitstempel nicht gefunden", e);
          }
        }
        if (!cfg) {
          cfg = await fetchValueConfig();
        }

        if (cfg) {
          const blocks = campaignDurationBlocks(ad.start_date, ad.end_date);
          const derivedCars = Math.max(1, Math.floor((ad.budget || cfg.car_price) / (cfg.car_price * blocks)));
          setCars(derivedCars);
          setBudget(derivedCars * cfg.car_price * blocks);
          setValueConfig(cfg);
          setCarPrice(cfg.car_price);
        }

        // 2. Fetch metadata (areas, day_times)
        const metaRes = await fetchWithAuth(buildApiUrl(`/ad/metadata?ad_id=${encodeURIComponent(editId)}`));
        if (!metaRes.ok) {
          const text = await metaRes.text().catch(() => "");
          throw new Error(`Konnte Geotargeting nicht laden (${metaRes.status})${text ? `: ${text}` : ""}`);
        }

        const meta = await metaRes.json();
        const adMeta = meta?.ad_metadata ?? meta?.metadata ?? meta ?? {};
        const locs = (Array.isArray(adMeta.locations) ? adMeta.locations : []).map((a: any) => ({
          id: uuid(),
          latitude: Number(a.latitude),
          longitude: Number(a.longitude),
          radius: Number(a.radius),
        })).filter((a: Area) => Number.isFinite(a.latitude) && Number.isFinite(a.longitude) && Number.isFinite(a.radius));

        setAreas(locs);
        setSelectedAreaId(locs[0]?.id ?? null);
        setDayTimes(
          (Array.isArray(adMeta.day_times) && adMeta.day_times.length
            ? adMeta.day_times
            : [{ start_time: "07:00", end_time: "10:00" }]
          ).map((r: any) => ({
            start_time: toHHMM(String(r.start_time ?? "")),
            end_time: toHHMM(String(r.end_time ?? "")),
          }))
        );
        // Try load existing creative image for preview
        try {
          const imgRes = await fetchWithAuth(buildApiUrl(`/ad/image?ad_id=${encodeURIComponent(editId)}`));
          if (imgRes.ok) {
            const blob = await imgRes.blob();
            const url = URL.createObjectURL(blob);
            setCreativePreviewUrl(url);
          }
        } catch {}
      } catch (e: any) {
        setErr(e.message || String(e));
      }
    })();
  }, [editId, fetchWithAuth]);

  // Revoke object URL when file changes or component unmounts
  useEffect(() => {
    return () => {
      if (creativePreviewUrl) URL.revokeObjectURL(creativePreviewUrl);
    };
  }, [creativePreviewUrl]);

  // UI
  const sidebarClass = isMobile ? `portal-sidebar-drawer${navOpen ? " open" : ""}` : "";

  return (
    <div className="portal-root">
      <PortalSidebar className={sidebarClass} mobile={isMobile} onClose={() => setNavOpen(false)} />
      {navOpen && isMobile && (
        <button
          className="portal-drawer-backdrop"
          onClick={() => setNavOpen(false)}
          aria-label="Navigation schließen"
        />
      )}
      <main className="portal-main">
        <div className="portal-topbar portal-topbar-wide">
          <div className="portal-topbar-left" style={{display:'flex',alignItems:'center',gap:10}}>
            {isMobile && (
              <button
                className="portal-mobile-menu"
                onClick={() => setNavOpen(true)}
                aria-label="Navigation öffnen"
              >
                ☰
              </button>
            )}
            <div className="portal-title">{editId ? "Kampagne bearbeiten" : "Kampagne erstellen"}</div>
          </div>
          <div className="portal-search">
            <button className="btn light" onClick={() => navigate("/")}>← Zur Übersicht</button>
          </div>
        </div>

        {err && (
          <div style={{background:'#c14725',padding:12,borderRadius:12}}>{err}</div>
        )}

        {step === 1 && (
          <motion.div layout className="portal-grid-2">
            <div className="portal-panel">
              <h2 className="portal-section-title">Kampagnendetails</h2>
            <div>
              <div className="portal-field">
                <label className="portal-label">Titel</label>
                <input
                  className="portal-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Herbstaktion"
                />
              </div>
              <div className="portal-field">
                <label className="portal-label">Beschreibung</label>
                <textarea
                  className="portal-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Markenbekanntheit in CH-Städten"
                />
              </div>
              <div className="portal-grid-2">
                <div className="portal-field">
                  <label className="portal-label">Startdatum (TT/MM/JJJJ)</label>
                  <input
                    type="date"
                    className="portal-input"
                    min={isAdminEdit ? undefined : toISODate(addDays(new Date(), 28))}
                    value={ddmmyyyyToISO(startDate)}
                    onChange={(e) => setStartDate(isoToDDMMYYYY(e.target.value))}
                  />
                </div>
                <div className="portal-field">
                  <label className="portal-label">Enddatum (TT/MM/JJJJ)</label>
                  <input
                    type="date"
                    className="portal-input"
                    value={ddmmyyyyToISO(endDate)}
                    onChange={(e) => setEndDate(isoToDDMMYYYY(e.target.value))}
                  />
                </div>
              </div>
              <div className="portal-grid-3">
                <div className="portal-field">
                  <label className="portal-label">Anzahl Fahrzeuge</label>
                  <div className="portal-input-wrap">
                    <input
                      type="number"
                      className="portal-input"
                      min={1}
                      value={cars}
                      onChange={(e) => setCars(Math.max(1, Number(e.target.value) || 1))}
                      placeholder="1"
                    />
                  </div>
                  <div className="portal-note">
                    Budget: {Number(budget).toLocaleString('de-CH')} CHF ({campaignDurationBlocks(startDate, endDate)} × 28-Tage-Block, à {carPrice ? carPrice.toLocaleString('de-CH') : "-"} CHF pro Fahrzeug)
                  </div>
                </div>
                <div className="portal-field">
                  <label className="portal-label">Fahrzeugtyp</label>
                  <select
                    className="portal-input"
                    value={campaignFuelTarget}
                    onChange={(e) => setCampaignFuelTarget(e.target.value as CampaignFuelTarget)}
                  >
                    {CAMPAIGN_FUEL_TARGET_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="portal-note">
                    Nur Fahrerinnen und Fahrer mit passenden Fahrzeugen sehen diese Kampagne.
                  </div>
                </div>
                <div className="portal-field" style={{display:"none"}}>
                  <label className="portal-label" style={{display:"none"}}>CPK</label>
                  <input
                    type="number"
                    className="portal-input"
                    value={valueConfig?.cpk ?? ""}
                    readOnly
                    hidden
                  />
                </div>
                <label className="portal-field" style={{display:'none',alignItems:'center',gap:8, marginTop: '26px'}} hidden>
                  <input type="checkbox" checked={true} onChange={(e) => setExclusive(e.target.checked)} hidden/>
                  <span>Exklusiv</span>
                </label>
              </div>
              <div className="portal-field">
                <label className="portal-label">Creative (SVG oder PNG)</label>
                <input
                  type="file"
                  accept="image/svg+xml,image/png,.svg,.png"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setCreativeFile(f);
                    if (creativePreviewUrl) URL.revokeObjectURL(creativePreviewUrl);
                    setCreativePreviewUrl(f ? URL.createObjectURL(f) : null);
                  }}
                  className="portal-file"
                />
                <div className="portal-note" style={{marginTop: 8}}>
                  Siehe auch{" "}
                  <a
                    className="portal-link"
                    href="http://werbeauto.ch/druckdaten"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Creative Guidelines
                  </a>
                </div>
                {creativePreviewUrl && (
                  <div style={{marginTop: 8}}>
                    <div className="portal-note" style={{marginBottom: 4}}>Vorschau</div>
                    <div style={{background:'#fff',border:'1px solid #e5efe9',borderRadius:16,padding:8,maxHeight:260,overflow:'auto'}}>
                      <img
                        src={creativePreviewUrl}
                        alt={`${getCreativeMediaType(creativeFile) === "image/png" ? "PNG" : "Creative"} Vorschau`}
                        style={{maxWidth:'100%'}}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="portal-field">
                <label className="portal-label">Weiterleitungs-URL</label>
                <input
                  className="portal-input"
                  value={redirectUrl}
                  onChange={(e) => setRedirectUrl(e.target.value)}
                  placeholder="https://example.com"
                />
                <div className="portal-note">Muss mit http:// oder https:// beginnen</div>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="btn cta"
                onClick={() => {
                  const msg = validateStep1();
                  if (msg) return setErr(msg);
                  setStep(2);
                }}
              >
                Weiter: Zielgruppen →
              </button>
            </div>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div layout className="space-y-4">
          <div className="portal-panel" style={{overflow:'hidden'}}>
            <MapContainer
              whenCreated={(m) => (mapRef.current = m)}
              center={[47.3769, 8.5417]}
              zoom={12}
              style={{ height: 420, width: "100%" }}
              className="cursor-crosshair"
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
              <ClickToAddCircle onAdd={handleAddArea} />
              {areas.map((a) => (
                <Circle
                  key={a.id}
                  center={[a.latitude, a.longitude]}
                  radius={a.radius}
                  pathOptions={{ opacity: a.id === selectedAreaId ? 1 : 0.3, weight: 2 }}
                  eventHandlers={{
                    click: () => setSelectedAreaId(a.id),
                  }}
                >
                  <Tooltip permanent>{Math.round(a.radius)} m</Tooltip>
                </Circle>
              ))}
              {/* transparent overlay to catch wheel events on selected circle */}
              {areas.map((a) => (
                <div key={a.id} />
              ))}
            </MapContainer>
          </div>

          {/* Areas table + controls */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{marginTop: 8}}>
            <div className="portal-panel lg:col-span-2">
              <div className="p-3 border-b font-medium">Werbegebiete</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2">#</th>
                      <th className="text-left p-2">Breitengrad</th>
                      <th className="text-left p-2">Längengrad</th>
                      <th className="text-left p-2">Radius (m)</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {areas.map((a, idx) => (
                      <tr
                        key={a.id}
                        className={
                          "border-t " +
                          (a.id === selectedAreaId ? "bg-white" : "bg-slate-50 opacity-70")
                        }
                        onClick={() => setSelectedAreaId(a.id)}
                      >
                        <td className="p-2">{idx + 1}</td>
                        <td className="p-2">{a.latitude.toFixed(5)}</td>
                        <td className="p-2">{a.longitude.toFixed(5)}</td>
                        <td className="p-2">
                          <input
                            type="range"
                            min={50}
                            max={5000}
                            step={10}
                            value={a.radius}
                            onChange={(e) => handleRadiusSlider(Number(e.target.value), a.id)}
                            onWheel={(e) => onWheelOverCircle(e as any, a.id)}
                          />
                          <div className="text-xs text-slate-600">{Math.round(a.radius)} m</div>
                        </td>
                        <td className="p-2 text-right">
                          <button className="text-red-600" onClick={() => removeArea(a.id)}>
                            Entfernen
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!areas.length && (
                      <tr>
                        <td colSpan={5} className="p-4 text-center text-slate-500">
                          Klicke auf die Karte, um ein Gebiet hinzuzufügen.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Day-times (global) */}
            <div className="portal-panel">
              <div className="font-medium mb-2">Zeitfenster</div>
              <div className="text-xs text-slate-600 mb-2">
                Gilt für die gesamte Kampagne (alle Gebiete). Das Backend akzeptiert aktuell eine gemeinsame Liste von Zeitfenstern.
              </div>
              <div className="space-y-2">
                {dayTimes.map((r, i) => (
                  <div key={i} className="grid grid-cols-5 gap-2 items-center">
                    <label className="text-xs text-slate-600">Startzeit</label>
                    <input
                      className="portal-input"
                      placeholder="07:00"
                      value={r.start_time}
                      onChange={(e) =>
                        setDayTimes((prev) => prev.map((x, idx) => (idx === i ? { ...x, start_time: e.target.value } : x)))
                      }
                    />
                    <label className="text-xs text-slate-600">Endzeit</label>
                    <input
                      className="portal-input"
                      placeholder="10:00"
                      value={r.end_time}
                      onChange={(e) =>
                        setDayTimes((prev) => prev.map((x, idx) => (idx === i ? { ...x, end_time: e.target.value } : x)))
                      }
                    />
                    <div className="text-right">
                      <button
                        className="text-red-600 text-sm"
                        onClick={() => setDayTimes((prev) => prev.filter((_, idx) => idx !== i))}
                      >
                        Entfernen
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  className="btn light"
                  onClick={() => setDayTimes((prev) => [...prev, { start_time: "16:30", end_time: "19:30" }])}
                >
                  + Zeitfenster hinzufügen
                </button>
              </div>
            </div>
          </div>

            <div className="portal-actions-bar">
              <button className="btn light" onClick={() => setStep(1)}>
                ← Zurück
              </button>
              <button
                className="btn cta"
                onClick={handleSubmit}
                disabled={busy}
              >
                {busy ? "Übermittle…" : editId ? "Kampagne speichern" : "Kampagne erstellen"}
              </button>
            </div>
          </motion.div>
        )}

        {step === 3 && done && (
          <motion.div layout className="portal-panel" style={{padding:24}}>
          <h2 className="text-lg font-medium mb-2">Kampagne erstellt</h2>
          <p className="text-slate-700">
            Deine Kampagne wurde erstellt und die Targeting‑Metadaten gespeichert. Du findest sie in deiner Übersicht.
          </p>
          <div className="mt-4 flex gap-2">
            <button className="btn dark" onClick={() => navigate("/")}>Zur Übersicht</button>
          </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default CampaignWizard;

function toHHMM(str: string) {
  // Accepts "HH:MM" or "HH:MM:SS" and returns "HH:MM"
  return str && str.length >= 5 ? str.slice(0, 5) : str;
}
